import { useState, useEffect, useMemo, useRef } from 'react';
import { AxesValuProps, ITabTilesAndTheirFilters } from './ChartAxesInterfaces';
import { IFilter, IFilterPanel } from '../DataSet/types/BottomBarInterfaces';
import { modifyFilter, getCalculationByUid } from '../CommonFunctions/CommonFunctions';
import { checkMinRequiredCards } from './chartDataFunctions/checkMinRequiredCards';
import FetchData from '../ServerCall/FetchData';
import debounce from 'lodash/debounce';
import { makeServiceCall } from './chartDataFunctions/makeServiceCall';
import { AlertColor } from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import {
  callQueryAPI,
  storeErrorDetails,
  storeServerData,
  updateChartData,
  updateRichTextDynamicValue,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { useDispatch } from 'react-redux';
import { updateChartDataForDm } from '../../redux/DynamicMeasures/DynamicMeasuresActions';
import { canReUseData, toggleAxesEdited } from '../../redux/ChartPoperties/ChartPropertiesActions';
import { chartFilterGroupEdited } from '../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import {
  callQueryAPIDashboard,
  setDashTileSwitched,
} from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { dashBoardFilterGroupsEdited } from '../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';
import { formatChartLabelValue } from '../ChartOptions/Format/NumberFormatter';
import { isInvalidValue } from './chartDataFunctions/getChartDataFunction';
import { sortChartData } from './chartDataFunctions/sortChartData';
import { filterShowHideData } from '../ChartOptions/ShowHide/filterShowHideData';
import { TDimensionType } from '../../types/types';
import { AxisType, DimensionType } from '../CommonFunctions/aliases';

/**
 * This is a custom hook containing a useEffect which is responsible to make a change in chart/table/graph to filter and show outcomes accordingly.
 */
const useChartData = (
  screenFrom: string,
  tabId: number,
  chartProp: any,
  _propKey: string,
  link: string,
  setShowSnackBar: React.Dispatch<React.SetStateAction<boolean>>,
  setOpenAlert: React.Dispatch<React.SetStateAction<boolean>>,
  setTestMessage: React.Dispatch<React.SetStateAction<string>>,
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('success');

  const searchParams = new URLSearchParams(window.location.search);
  const tenantId = searchParams.get('tenantId');

  const {
    tabState,
    calculations,
    chartProperties,
    chartControls,
    tabTileProps,
    dynamicMeasureState,
    dashBoardGroup,
    token,
    chartGroup,
    tooltipFormats,
    tvSettings,
  } = useSelector((state: RootState) => ({
    tabState: state.tabState,
    calculations: state.calculations,
    chartProperties: state.chartProperties,
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    dynamicMeasureState: state.dynamicMeasuresState,
    dashBoardGroup: state.dashBoardFilterGroup,
    token: state.isLogged.accessToken,
    chartGroup: state.chartFilterGroup,
    tooltipFormats: state.chartControls?.properties[_propKey]?.formatOptions?.tooltipFormats,
    tvSettings: state.tabTileProps.tvSettings,
  }));
  const chartType = chartProperties.properties[_propKey]?.chartType;

  const savedCalculations = useMemo(
    () => calculations?.savedCalculations,
    [calculations?.savedCalculations],
  );
  let _chartAxes: any = [];
  if (chartProp.chartAxes) {
    _chartAxes = chartProp.chartAxes;
  } else {
    _chartAxes = chartProp.properties[_propKey].chartAxes;
  }
  const { selectedTabArray, dataRefreshEnableds, dataRefreshUnit, dataRefreshTime } = tvSettings;

  const dispatch = useDispatch();
  let refreshTime;
  if (dataRefreshUnit === 'Minutes') {
    refreshTime = dataRefreshTime * 60 * 1000;
  } else {
    refreshTime = dataRefreshTime * 60 * 60 * 1000;
  }
  const intervalRef = useRef<{ [key: string]: NodeJS.Timeout }>({});

  /**
   * every time chartAxes or chartType is changed, check if
   * new data must be obtained from server
   * check for minimum requirements in each dropzone for the given chart type
   * if not reset the data
   * the chart Data is stored in  chartData of respective tabTile if chart type is not richText else stored in richText
   *
   * if screen is dashboard  get all the tabTileId are being displayed in dashboard
   * "tabTilesAndTheirFilters" object is created to store list of filters to be applied on a tabTileID
   * key->tabTileId ,value->list of filters
   *
   * for each tiles in dashboard iterate over filterGroupTabTiles(object stores the list of tiles(value) on wihich filtergroupID(key) will, be applied ) object to get the filterGroupIDs to br applied on  the current tile
   * if a filter group is to be applied on a tile get list of filters  from chartFilterGroup  and store it
   *
   * get list of  tabTileID which does not have any filter group to be applied on it as filterPanels should be empty for those tiles
   *
   * now fetch data  for tiles and store them in respective global state
   */

  const dashboardShouldAllMatch =
    typeof dashBoardGroup.any_condition_match === 'boolean'
      ? !dashBoardGroup.any_condition_match
      : false;

  // Helper function to get extra measure fields from tooltip for a specific tile
  const getExtraMeasureFields = (tabTileID: string) => {
    const tileTooltipFormats = chartControls.properties[tabTileID]?.formatOptions?.tooltipFormats;
    if (!tileTooltipFormats?.extraMeasureField?.[0]?.fields?.length) {
      return { withOverride: [], withoutOverride: [] };
    }

    const extraFields = JSON.parse(JSON.stringify(tileTooltipFormats.extraMeasureField[0].fields));
    const withOverride: any[] = [];
    const withoutOverride: any[] = [];

    extraFields.forEach((field: any) => {
      if (field.override) {
        withOverride.push(field);
      } else {
        withoutOverride.push(field);
      }
    });

    return { withOverride, withoutOverride };
  };

  const fetchDataforDashboard = async (tabId: number) => {
    if (tabTileProps.callQueryAPIDashboard === false) {
      return;
    }
    const tilesInDashboard = tabState.tabs[`${tabId}`].tilesInDashboard;
    const tabTilesAndTheirFilters: ITabTilesAndTheirFilters = {};

    const getCalculationInfoByUid = (uid: string) => {
      if (!savedCalculations || !uid) return null;

      return savedCalculations.find((item: any) => item.uuid === uid);
    };

    tilesInDashboard.forEach((tabTileID: string) => {
      Object.keys(dashBoardGroup.filterGroupTabTiles).forEach((filterGroupID: string) => {
        const selectedDsId = chartProperties.properties[tabTileID]?.selectedDs.id;
        if (dashBoardGroup.filterGroupTabTiles[filterGroupID].includes(tabTileID)) {
          if (selectedDsId !== chartGroup.groups[filterGroupID]?.dataSetId) {
            setShowSnackBar(true);
            setMessage(`${chartGroup.groups[filterGroupID]?.name} is ignored.`);
            return;
          }
          let filters: IFilter[] = [];
          const syncFilterEnabled = chartGroup.groups[filterGroupID].syncFilter;
          chartGroup.groups[filterGroupID].filters.forEach((filter: any) => {
            if (filter.fieldtypeoption === 'Pick List') {
              if (
                filter.userSelection.length === 0 ||
                (filter.userSelection.includes('(All)') && !syncFilterEnabled)
              )
                return;
            }
            if (
              filter.fieldtypeoption === 'Pick List' &&
              (filter.userSelection?.length === 0 || filter.userSelection?.includes('(All)'))
            ) {
              if (syncFilterEnabled && filter.userSelection?.includes('(All)'))
                if (
                  !syncFilterEnabled &&
                  filter.filterTypeTillDate === 'enabled' &&
                  !filter.exprTypeTillDate
                )
                  return;
            }
            if (
              filter.exprType === 'between' &&
              (isInvalidValue(filter.greaterThanOrEqualTo) ||
                isInvalidValue(filter.lessThanOrEqualTo))
            ) {
              return false;
            } else if (filter.exprType === 'blank') return true;
            else if (
              filter.exprType !== 'between' &&
              filter.fieldtypeoption === 'Search Condition' &&
              isInvalidValue(filter.exprInput)
            ) {
              return false;
            }
            filters.push(modifyFilter(filter, savedCalculations));
          });
          if (filters.length > 0) {
            if (tabTilesAndTheirFilters[tabTileID]) {
              tabTilesAndTheirFilters[tabTileID].push(...filters);
            } else {
              tabTilesAndTheirFilters[tabTileID] = filters;
            }
          }
        }
      });
    });

    Object.keys(tabTilesAndTheirFilters).forEach((tabTileID: string) => {
      if (chartProperties.properties[`${tabTileID}`].chartType === 'richText') return;
      const chartProp: any =
        chartProperties.properties[`${tabTileID}`].chartType === 'richText'
          ? dynamicMeasureState.dynamicMeasureProps?.[tabTileID.split('.')[0]]?.[
              tabTileID.split('.')[1]
            ]?.[`${tabTileID.split('.')[1]}.${dynamicMeasureState.selectedDynamicMeasureId}`]
          : chartProperties.properties[`${tabTileID}`];

      let serverCall = false;
      const chartType = chartProperties.properties[`${tabTileID}`].chartType;

      if (
        // chartProp.axesEdited ||
        // chartGroup.chartFilterGroupEdited ||
        // dashBoardGroup.dashBoardGroupEdited ||
        chartControls.properties[tabTileID].callQueryAPI ||
        chartControls.properties[tabTileID].chartData.length === 0
      ) {
        if (chartProp.reUseData) {
          serverCall = false;
        } else {
          var minReq = checkMinRequiredCards(
            chartProp,
            chartProperties.properties[tabTileID].chartType,
          );

          if (minReq) {
            serverCall = true;
            dispatch(storeServerData(tabTileID, ''));
          } else {
            if (chartProperties.properties[tabTileID].chartType === 'richText') {
              dispatch(updateChartDataForDm(''));
            } else {
              dispatch(updateChartData(tabTileID, ''));
            }
          }
        }
      }
      const filters = tabTilesAndTheirFilters[tabTileID];

      const chartFilters = chartProp.chartAxes.filter(
        (axes: AxesValuProps) => axes.name === 'Filter',
      );
      const appliedChartFilters = chartFilters[0]?.fields;
      const payloadChartFilter: IFilter[] = [];
      appliedChartFilters?.forEach((_filter: any) => {
        if (
          _filter.fieldtypeoption === 'Pick List' &&
          (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
        ) {
          if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
        }
        if (
          _filter.exprType === 'between' &&
          (isInvalidValue(_filter.greaterThanOrEqualTo) ||
            isInvalidValue(_filter.lessThanOrEqualTo))
        ) {
          return false;
        } else if (_filter.exprType === 'blank') return true;
        else if (
          _filter.exprType !== 'between' &&
          _filter.fieldtypeoption === 'Search Condition' &&
          isInvalidValue(_filter.exprInput)
        ) {
          return false;
        }
        payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
      });

      const filterPanels: IFilterPanel[] = [
        {
          panelName: 'dashboardFilters',
          shouldAllConditionsMatch: dashboardShouldAllMatch,
          filters: [...filters],
        },
      ];
      if (payloadChartFilter.length > 0) {
        filterPanels.push({
          panelName: 'chartFilters',
          shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
          filters: payloadChartFilter,
        });
      }

      const currentOperator =
        chartControls?.properties[_propKey]?.rowSecurity?.currentOperator?.toLowerCase();

      if (
        chartControls?.properties[tabTileID]?.rowSecurity?.filterPanels?.[currentOperator]?.filters
          .length > 0
      ) {
        const rawFilters =
          chartControls?.properties?.[tabTileID]?.rowSecurity?.filterPanels?.[currentOperator]
            ?.filters || [];

        const enhancedFilters = rawFilters.map((filter: any) => {
          const calculation = getCalculationInfoByUid(filter.calculationUUID);
          return {
            ...filter,
            ...(calculation && {
              calculatedField: [
                {
                  calculatedFieldName: calculation.name.split(' ').join(' '),
                  isAggregated: calculation.isAggregated,
                  fields: calculation.calculationInfo.fields,
                  flows: calculation.calculationInfo.flows,
                  conditionFilters: calculation.calculationInfo.conditionFilters,
                  overrided: calculation.calculationInfo.overrideCount > 0,
                },
              ],
              isCalculatedField: true,
            }),
            filterType: `row_level_security_${currentOperator.toLowerCase()}`,
            operator: 'in',
            shouldExclude: false,
          };
        });

        const payloadRLS = {
          filters: enhancedFilters,
          panelName: 'rowLevelSecurityFilters',
          shouldAllConditionsMatch: true,
        };
        filterPanels.push(payloadRLS);
      }

      if (
        chartControls?.properties[tabTileID]?.columnSecurity?.filterPanels?.[currentOperator]
          ?.filters.length > 0
      ) {
        const rawFilters =
          chartControls?.properties?.[tabTileID]?.columnSecurity?.filterPanels?.[currentOperator]
            ?.filters || [];

        const enhancedFilters = rawFilters.map((filter: any) => {
          const calculation = getCalculationInfoByUid(filter.calculationUUID);
          return {
            ...filter,
            ...(calculation && {
              calculatedField: [
                {
                  calculatedFieldName: calculation.name.split(' ').join(' '),
                  isAggregated: calculation.isAggregated,
                  fields: calculation.calculationInfo.fields,
                  flows: calculation.calculationInfo.flows,
                  conditionFilters: calculation.calculationInfo.conditionFilters,
                  overrided: calculation.calculationInfo.overrideCount > 0,
                },
              ],
              isCalculatedField: true,
            }),
            ...(filter.filterType !== 'column_level_security_column' && {
              filterType: `column_level_security_${currentOperator.toLowerCase()}`,
            }),
            operator: 'in',
            shouldExclude: false,
          };
        });
        const payloadCLS = {
          filters: enhancedFilters,
          panelName: 'columnLevelSecurityFilters',
          shouldAllConditionsMatch: true,
        };
        const hasMeasureMapping =
          enhancedFilters.length > 0 &&
          enhancedFilters.find(
            (filter: any) =>
              filter?.filterType === 'column_level_security_column' && filter?.mapping?.length > 0,
          );
        if (hasMeasureMapping) {
          filterPanels.push(payloadCLS);
        }
      }

      let payLoad: any[] = [];
      const measures = chartProp.chartAxes.filter((axes: AxesValuProps) =>
        ['Measure', 'X Axis', 'Y Axis'].includes(axes.name),
      );
      let idx = 1;
      const allMeasures = measures.flatMap((measure: any, measureIndex: number) =>
        measure.fields.map((el: any) => ({
          ...el,
          measureOrder: idx++,
        })),
      );
      const nonOverrideMeasureFields = allMeasures
        .map((el: any) => (!el.override ? el : null))
        .filter((field: any) => field !== null);
      const overrideMeasureFields = allMeasures
        .map((el: any) => (el.override ? el : null))
        .filter((field: any) => field !== null);

      // Get extra measures from tooltip and add to measure arrays
      const extraMeasures = getExtraMeasureFields(tabTileID);
      nonOverrideMeasureFields.push(...extraMeasures.withoutOverride);
      overrideMeasureFields.push(...extraMeasures.withOverride);

      let measureOrder = 1;
      const non_override_measures_for_payload = nonOverrideMeasureFields.map((el: any) => {
        var formattedField: any = {
          dataType: el.dataType,
          measureId: el.uId,
          fieldName: el.fieldname,
          displayName: el.displayname,
          rollupDepth: false,
          measureOrder: el.measureOrder,
          aggr: el.agg,
          tableId: el.tableId,
          ...getCalculationByUid(
            el.SavedCalculationUUID,
            chartType,
            chartProp.chartAxes,
            savedCalculations,
          ),
        };
        if (el.windowfn) {
          //Function used to convert all the values of windowFunction in camelCase
          function toCamelCase(str: any) {
            return str
              .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                return index === 0 ? word.toLowerCase() : word.toUpperCase();
              })
              .replace(/\s+/g, '');
          }

          switch (el.windowfn.windowFnOptions) {
            case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.windowFnOptions),
                    toCamelCase(el.windowfn.rank),
                    toCamelCase(el.windowfn.order),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.windowFnOptions),
                      toCamelCase(el.windowfn.rank),
                      toCamelCase(el.windowfn.order),
                    ];
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.standingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.standingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.standingRowIndex,
                        el.windowfn.standingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.standingRowIndex];
                  }
                }
              }

              break;
            case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.windowFnOptions),
                    toCamelCase(el.windowfn.slidingAggregation),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.windowFnOptions),
                      toCamelCase(el.windowfn.slidingAggregation),
                    ];
                  }
                }
              }

              //sending windowFnOption for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnOption = [el.windowfn.slidingPreInc];
                  if (![undefined, null].includes(el.windowfn.slidingNextInc))
                    formattedField.windowFnOption.push(el.windowfn.slidingNextInc);
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [el.windowfn.slidingPreInc];
                    if (![undefined, null].includes(el.windowfn.slidingNextInc)) {
                      formattedField.windowFnOption.push(el.windowfn.slidingNextInc);
                    }
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.slidingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.slidingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.slidingRowIndex,
                        el.windowfn.slidingColumnIndex,
                        ['rowwise'].includes(el.windowfn.slidingSlideDirection) ? 0 : 1,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.slidingRowIndex];
                  }
                }
              }

              break;
            case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.percentage),
                    ['First', 'Last'].includes(el.windowfn.standingSlidingReferenceWn)
                      ? toCamelCase(el.windowfn.standingSlidingReferenceWn)
                      : toCamelCase(el.windowfn.standingSlidingAggregation),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.percentage),
                      ['First', 'Last'].includes(el.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(el.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(el.windowfn.standingSlidingAggregation),
                    ];
                  }
                }
              }

              //sending windowFnOption for all charts except richtext
              if (['PNC'].includes(el.windowfn.standingSlidingReferenceWn)) {
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [el.windowfn.standingSlidingPreInc];
                    if (![undefined, null].includes(el.windowfn.standingSlidingNextInc)) {
                      formattedField.windowFnOption.push(el.windowfn.standingSlidingNextInc);
                    }
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [el.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(el.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(el.windowfn.standingSlidingNextInc);
                      }
                    }
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.standingSlidingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.standingSlidingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.standingSlidingRowIndex,
                        el.windowfn.standingSlidingColumnIndex,
                        ['rowwise'].includes(el.windowfn.standingSlidingSlideDirection) ? 0 : 1,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.standingSlidingRowIndex];
                  }
                }
              }
              break;
          }
        }
        return formattedField;
      });
      if (
        ['funnel', 'gauge', 'simplecard', 'richText'].includes(
          chartProperties.properties[`${tabTileID}`].chartType,
        )
      ) {
        payLoad = [
          {
            dimensions: [],
            filterPanels,
            measures: non_override_measures_for_payload,
          },
        ];

        overrideMeasureFields.forEach((field: any, idx: number) => {
          const isDisabledDashFilter = field.disableReportFilterForOverride;
          const chartFilters = field.override.filter(
            (axes: AxesValuProps) => axes.name === 'Filter',
          );
          const formattedField = {
            dataType: field.dataType,
            measureId: field.uId,
            fieldName: field.fieldname,
            displayName: field.displayname,
            rollupDepth: false,
            measureOrder: field.measureOrder,
            aggr: field.agg,
            tableId: field.tableId,
            ...getCalculationByUid(
              field.SavedCalculationUUID,
              chartType,
              chartProp.chartAxes,
              savedCalculations,
            ),
            ...(field.windowfn
              ? {
                  windowFn: [] as any[],
                  windowFnOption: [] as any[],
                  windowFnMatrix: [] as any[],
                  windowFnPartition: [] as any[],
                }
              : {}),
          };
          if (field.windowfn) {
            //Function used to convert all the values of windowFunction in camelCase
            function toCamelCase(str: any) {
              return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                  return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                .replace(/\s+/g, '');
            }

            switch (field.windowfn.windowFnOptions) {
              case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.rank),
                      toCamelCase(field.windowfn.order),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.rank),
                        toCamelCase(field.windowfn.order),
                      ];
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.standingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingRowIndex,
                          field.windowfn.standingColumnIndex,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                    }
                  }
                }

                break;
              case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.slidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.slidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                    if (![undefined, null].includes(field.windowfn.slidingNextInc))
                      formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                      if (![undefined, null].includes(field.windowfn.slidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.slidingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.slidingRowIndex,
                          field.windowfn.slidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.slidingSlideDirection) ? 0 : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                    }
                  }
                }

                break;
              case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.percentage),
                      ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(field.windowfn.standingSlidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.percentage),
                        ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                          ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                          : toCamelCase(field.windowfn.standingSlidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['PNC'].includes(field.windowfn.standingSlidingReferenceWn)) {
                  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                      }
                    }
                  } else {
                    if (
                      !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(
                        chartType,
                      )
                    ) {
                      if (_chartAxes[1].fields.length === 0) {
                      } else {
                        formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                        if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                          formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                        }
                      }
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        field.windowfn.standingSlidingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingSlidingRowIndex,
                          field.windowfn.standingSlidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.standingSlidingSlideDirection)
                            ? 0
                            : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                    }
                  }
                }
                break;
            }
          }
          let measures = [formattedField];
          const filterPanels: IFilterPanel[] = [];
          const appliedChartFilters = chartFilters[0]?.fields;
          const payloadChartFilter: IFilter[] = [];
          appliedChartFilters?.forEach((_filter: any) => {
            if (
              _filter.fieldtypeoption === 'Pick List' &&
              (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
            ) {
              if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
            }
            if (
              _filter.exprType === 'between' &&
              (isInvalidValue(_filter.greaterThanOrEqualTo) ||
                isInvalidValue(_filter.lessThanOrEqualTo))
            ) {
              return false;
            } else if (_filter.exprType === 'blank') return true;
            else if (
              _filter.exprType !== 'between' &&
              _filter.fieldtypeoption === 'Search Condition' &&
              isInvalidValue(_filter.exprInput)
            ) {
              return false;
            }
            payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
          });
          if (payloadChartFilter.length > 0) {
            filterPanels.push({
              panelName: 'chartFilters',
              shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
              filters: payloadChartFilter,
            });
          }

          // skip report filters for measure this if ignore report filter enabled
          if (!isDisabledDashFilter) {
            filterPanels.push({
              panelName: 'dashboardFilters',
              shouldAllConditionsMatch: dashboardShouldAllMatch,
              filters: [...filters],
            });
          }
          payLoad.push({
            dimensions: [],
            filterPanels: filterPanels,
            measures: measures,
          });
        });
      } else {
        const dimensions = chartProperties.properties[`${tabTileID}`].chartAxes.filter(
          (axes: AxesValuProps) => !['Measure', 'Filter', 'X Axis', 'Y Axis'].includes(axes.name),
        );
        const chartFilters = chartProperties.properties[`${tabTileID}`].chartAxes.filter(
          (axes: AxesValuProps) => axes.name === 'Filter',
        );

        payLoad = [
          {
            measures: non_override_measures_for_payload,
            dimensions: dimensions.flatMap((axes: AxesValuProps) => {
              let dimensionType: TDimensionType = 'dim';

              if (axes.name === AxisType.Row) dimensionType = DimensionType.ROW;
              else if (axes.name === AxisType.Column) dimensionType = DimensionType.COLUMN;
              else if (axes.name === AxisType.Dimension) dimensionType = DimensionType.DIMENSION;
              else if (axes.name === AxisType.SplitBy) dimensionType = DimensionType.SPLIT;
              else dimensionType = DimensionType.DIMENSION;
              return axes.fields.map((filter: any) => ({
                dataType: filter.dataType,
                dimensionId: filter.uId,
                fieldName: filter.fieldname,
                displayName: filter.displayname,
                tableId: filter.tableId,
                rollupDepth: false,
                dimensionType,
                timeGrain: filter.timeGrain /*  PRAKASH 14Nov2024 Demo Changes  */,
                ...getCalculationByUid(
                  filter.SavedCalculationUUID,
                  chartType,
                  dimensions,
                  savedCalculations,
                ),
              }));
            }),
            filterPanels: filterPanels,
          },
        ];

        overrideMeasureFields.forEach((field: any, idx: number) => {
          const isDisabledDashFilter = field.disableReportFilterForOverride;
          const dimensions = field.override.filter(
            (axes: AxesValuProps) => !['Measure', 'Filter', 'X Axis', 'Y Axis'].includes(axes.name),
          );
          const chartFilters = field.override.filter(
            (axes: AxesValuProps) => axes.name === 'Filter',
          );
          const formattedField = {
            dataType: field.dataType,
            measureId: field.uId,
            fieldName: field.fieldname,
            displayName: field.displayname,
            rollupDepth: false,
            measureOrder: field.measureOrder,
            aggr: field.agg,
            tableId: field.tableId,
            ...getCalculationByUid(
              field.SavedCalculationUUID,
              chartType,
              chartProp.chartAxes,
              savedCalculations,
            ),
            ...(field.windowfn
              ? {
                  windowFn: [] as any[],
                  windowFnOption: [] as any[],
                  windowFnMatrix: [] as any[],
                  windowFnPartition: [] as any[],
                }
              : {}),
          };
          if (field.windowfn) {
            //Function used to convert all the values of windowFunction in camelCase
            function toCamelCase(str: any) {
              return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                  return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                .replace(/\s+/g, '');
            }

            switch (field.windowfn.windowFnOptions) {
              case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.rank),
                      toCamelCase(field.windowfn.order),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.rank),
                        toCamelCase(field.windowfn.order),
                      ];
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.standingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingRowIndex,
                          field.windowfn.standingColumnIndex,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                    }
                  }
                }

                break;
              case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.slidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.slidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                    if (![undefined, null].includes(field.windowfn.slidingNextInc))
                      formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                      if (![undefined, null].includes(field.windowfn.slidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.slidingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.slidingRowIndex,
                          field.windowfn.slidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.slidingSlideDirection) ? 0 : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                    }
                  }
                }

                break;
              case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.percentage),
                      ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(field.windowfn.standingSlidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.percentage),
                        ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                          ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                          : toCamelCase(field.windowfn.standingSlidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['PNC'].includes(field.windowfn.standingSlidingReferenceWn)) {
                  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                      }
                    }
                  } else {
                    if (
                      !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(
                        chartType,
                      )
                    ) {
                      if (_chartAxes[1].fields.length === 0) {
                      } else {
                        formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                        if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                          formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                        }
                      }
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        field.windowfn.standingSlidingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingSlidingRowIndex,
                          field.windowfn.standingSlidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.standingSlidingSlideDirection)
                            ? 0
                            : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                    }
                  }
                }
                break;
            }
          }
          let measures = [formattedField];
          const filterPanels: IFilterPanel[] = [];
          const appliedChartFilters = chartFilters[0]?.fields;
          const payloadChartFilter: IFilter[] = [];
          appliedChartFilters?.forEach((_filter: any) => {
            if (
              _filter.fieldtypeoption === 'Pick List' &&
              (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
            ) {
              if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
            }
            if (
              _filter.exprType === 'between' &&
              (isInvalidValue(_filter.greaterThanOrEqualTo) ||
                isInvalidValue(_filter.lessThanOrEqualTo))
            ) {
              return false;
            } else if (_filter.exprType === 'blank') return true;
            else if (
              _filter.exprType !== 'between' &&
              _filter.fieldtypeoption === 'Search Condition' &&
              isInvalidValue(_filter.exprInput)
            ) {
              return false;
            }
            payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
          });
          if (payloadChartFilter.length > 0) {
            filterPanels.push({
              panelName: 'chartFilters',
              shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
              filters: payloadChartFilter,
            });
          }

          if (!isDisabledDashFilter) {
            filterPanels.push({
              panelName: 'dashboardFilters',
              shouldAllConditionsMatch: dashboardShouldAllMatch,
              filters: [...filters],
            });
          }

          payLoad.push({
            dimensions: dimensions.flatMap((axes: AxesValuProps) => {
              let dimensionType: TDimensionType = 'dim';

              if (axes.name === AxisType.Row) dimensionType = DimensionType.ROW;
              else if (axes.name === AxisType.Column) dimensionType = DimensionType.COLUMN;
              else if (axes.name === AxisType.Dimension) dimensionType = DimensionType.DIMENSION;
              else if (axes.name === AxisType.SplitBy) dimensionType = DimensionType.SPLIT;
              else dimensionType = DimensionType.DIMENSION;
              return axes.fields.map((filter: any) => ({
                dataType: filter.dataType,
                dimensionId: filter.uId,
                fieldName: filter.fieldname,
                displayName: filter.displayname,
                tableId: filter.tableId,
                rollupDepth: false,
                timeGrain: filter.timeGrain,
                dimensionType,
                ...getCalculationByUid(
                  filter.SavedCalculationUUID,
                  chartType,
                  dimensions,
                  savedCalculations,
                ),
              }));
            }),
            filterPanels: filterPanels,
            measures: measures,
          });
        });
      }

      if (serverCall) {
        (async () => {
          await fetchDataForTile(tabTileID, payLoad, chartProp, link);
        })();
      }
      if (screenFrom === 'TV_Display' && refreshTime > 0 && dataRefreshEnableds) {
        if (intervalRef.current[tabTileID]) {
          clearInterval(intervalRef.current[tabTileID]);
        }

        intervalRef.current[tabTileID] = setInterval(async () => {
          await fetchDataForTile(tabTileID, payLoad, chartProp, link);
        }, refreshTime);
      }
    });

    const tabTileIDsWithoutFilterGroups = tilesInDashboard.filter(
      (tabTileID: string) => !(tabTileID in tabTilesAndTheirFilters),
    );
    tabTileIDsWithoutFilterGroups.forEach((tabTileID: string) => {
      if (chartProperties.properties[`${tabTileID}`].chartType === 'richText') return;
      const chartProp: any =
        chartProperties.properties[`${tabTileID}`].chartType === 'richText'
          ? dynamicMeasureState.dynamicMeasureProps?.[tabTileID.split('.')[0]]?.[
              tabTileID.split('.')[1]
            ]?.[`${tabTileID.split('.')[1]}.${dynamicMeasureState.selectedDynamicMeasureId}`]
          : chartProperties.properties[`${tabTileID}`];
      let payLoad: any[] = [];

      let serverCall = false;
      if (
        // chartProp.axesEdited ||
        // chartGroup.chartFilterGroupEdited ||
        // dashBoardGroup.dashBoardGroupEdited ||
        // tabTileProps.isDashboardTileSwitched ||
        chartControls.properties[tabTileID].callQueryAPI ||
        chartControls.properties[tabTileID].chartData.length === 0
      ) {
        if (chartProp.reUseData) {
          serverCall = false;
        } else {
          var minReq = checkMinRequiredCards(
            chartProp,
            chartProperties.properties[tabTileID].chartType,
          );

          if (minReq) {
            serverCall = true;
            // dispatch(storeServerData(tabTileID, ''));
          } else {
            if (chartProperties.properties[tabTileID].chartType === 'richText') {
              dispatch(updateChartDataForDm(''));
            } else {
              dispatch(updateChartData(tabTileID, ''));
            }
          }
        }
      }

      const measures = chartProp.chartAxes.filter((axes: AxesValuProps) =>
        ['Measure', 'X Axis', 'Y Axis'].includes(axes.name),
      );
      let idx = 1;
      const allMeasures = measures.flatMap((measure: any, measureIndex: number) =>
        measure.fields.map((el: any) => ({
          ...el,
          measureOrder: idx++,
        })),
      );
      const nonOverrideMeasureFields = allMeasures
        .map((el: any) => (!el.override ? el : null))
        .filter((field: any) => field !== null);
      const overrideMeasureFields = allMeasures
        .map((el: any) => (el.override ? el : null))
        .filter((field: any) => field !== null);

      // Get extra measures from tooltip and add to measure arrays
      const extraMeasures = getExtraMeasureFields(tabTileID);
      nonOverrideMeasureFields.push(...extraMeasures.withoutOverride);
      overrideMeasureFields.push(...extraMeasures.withOverride);

      const filterPanels: IFilterPanel[] = [];
      const chartFilters = chartProp.chartAxes.filter(
        (axes: AxesValuProps) => axes.name === 'Filter',
      );
      const appliedChartFilters = chartFilters[0]?.fields;
      const payloadChartFilter: IFilter[] = [];
      appliedChartFilters?.forEach((_filter: any) => {
        if (
          _filter.fieldtypeoption === 'Pick List' &&
          (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
        ) {
          if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
        }
        if (
          _filter.exprType === 'between' &&
          (isInvalidValue(_filter.greaterThanOrEqualTo) ||
            isInvalidValue(_filter.lessThanOrEqualTo))
        ) {
          return false;
        } else if (_filter.exprType === 'blank') return true;
        else if (
          _filter.exprType !== 'between' &&
          _filter.fieldtypeoption === 'Search Condition' &&
          isInvalidValue(_filter.exprInput)
        ) {
          return false;
        }
        payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
      });
      if (payloadChartFilter.length > 0) {
        filterPanels.push({
          panelName: 'chartFilters',
          shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
          filters: payloadChartFilter,
        });
      }
      const currentOperator =
        chartControls?.properties[_propKey]?.rowSecurity?.currentOperator?.toLowerCase();

      if (
        chartControls?.properties[tabTileID]?.rowSecurity?.filterPanels?.[currentOperator]?.filters
          .length > 0
      ) {
        const rawFilters =
          chartControls?.properties?.[tabTileID]?.rowSecurity?.filterPanels?.[currentOperator]
            ?.filters || [];

        const enhancedFilters = rawFilters.map((filter: any) => {
          const calculation = getCalculationInfoByUid(filter.calculationUUID);
          return {
            ...filter,
            ...(calculation && {
              calculatedField: [
                {
                  calculatedFieldName: calculation.name.split(' ').join(' '),
                  isAggregated: calculation.isAggregated,
                  fields: calculation.calculationInfo.fields,
                  flows: calculation.calculationInfo.flows,
                  conditionFilters: calculation.calculationInfo.conditionFilters,
                  overrided: calculation.calculationInfo.overrideCount > 0,
                },
              ],
              isCalculatedField: true,
            }),
            filterType: `row_level_security_${currentOperator.toLowerCase()}`,
            operator: 'in',
            shouldExclude: false,
          };
        });

        const payloadRLS = {
          filters: enhancedFilters,
          panelName: 'rowLevelSecurityFilters',
          shouldAllConditionsMatch: true,
        };
        filterPanels.push(payloadRLS);
      }
      if (
        chartControls?.properties[tabTileID]?.columnSecurity?.filterPanels?.[currentOperator]
          ?.filters.length > 0
      ) {
        const rawFilters =
          chartControls?.properties?.[tabTileID]?.columnSecurity?.filterPanels?.[currentOperator]
            ?.filters || [];

        const enhancedFilters = rawFilters.map((filter: any) => {
          const calculation = getCalculationInfoByUid(filter.calculationUUID);
          return {
            ...filter,
            ...(calculation && {
              calculatedField: [
                {
                  calculatedFieldName: calculation.name.split(' ').join(' '),
                  isAggregated: calculation.isAggregated,
                  fields: calculation.calculationInfo.fields,
                  flows: calculation.calculationInfo.flows,
                  conditionFilters: calculation.calculationInfo.conditionFilters,
                  overrided: calculation.calculationInfo.overrideCount > 0,
                },
              ],
              isCalculatedField: true,
            }),
            ...(filter.filterType !== 'column_level_security_column' && {
              filterType: `column_level_security_${currentOperator.toLowerCase()}`,
            }),
            operator: 'in',
            shouldExclude: false,
          };
        });
        const payloadCLS = {
          filters: enhancedFilters,
          panelName: 'columnLevelSecurityFilters',
          shouldAllConditionsMatch: true,
        };
        const hasMeasureMapping =
          enhancedFilters.length > 0 &&
          enhancedFilters.find(
            (filter: any) =>
              filter?.filterType === 'column_level_security_column' && filter?.mapping?.length > 0,
          );
        if (hasMeasureMapping) {
          filterPanels.push(payloadCLS);
        }
      }
      let measureOrder = 1;
      const non_override_measures_for_payload = nonOverrideMeasureFields.map((el: any) => {
        var formattedField: any = {
          dataType: el.dataType,
          measureId: el.uId,
          fieldName: el.fieldname,
          displayName: el.displayname,
          rollupDepth: false,
          measureOrder: el.measureOrder,
          aggr: el.agg,
          tableId: el.tableId,
          ...getCalculationByUid(
            el.SavedCalculationUUID,
            chartType,
            chartProp.chartAxes,
            savedCalculations,
          ),
        };
        if (el.windowfn) {
          //Function used to convert all the values of windowFunction in camelCase
          function toCamelCase(str: any) {
            return str
              .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                return index === 0 ? word.toLowerCase() : word.toUpperCase();
              })
              .replace(/\s+/g, '');
          }

          switch (el.windowfn.windowFnOptions) {
            case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.windowFnOptions),
                    toCamelCase(el.windowfn.rank),
                    toCamelCase(el.windowfn.order),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.windowFnOptions),
                      toCamelCase(el.windowfn.rank),
                      toCamelCase(el.windowfn.order),
                    ];
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.standingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.standingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.standingRowIndex,
                        el.windowfn.standingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.standingRowIndex];
                  }
                }
              }

              break;
            case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.windowFnOptions),
                    toCamelCase(el.windowfn.slidingAggregation),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.windowFnOptions),
                      toCamelCase(el.windowfn.slidingAggregation),
                    ];
                  }
                }
              }

              //sending windowFnOption for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnOption = [el.windowfn.slidingPreInc];
                  if (![undefined, null].includes(el.windowfn.slidingNextInc))
                    formattedField.windowFnOption.push(el.windowfn.slidingNextInc);
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [el.windowfn.slidingPreInc];
                    if (![undefined, null].includes(el.windowfn.slidingNextInc))
                      formattedField.windowFnOption.push(el.windowfn.slidingNextInc);
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.slidingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.slidingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.slidingRowIndex,
                        el.windowfn.slidingColumnIndex,
                        ['rowwise'].includes(el.windowfn.slidingSlideDirection) ? 0 : 1,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.slidingRowIndex];
                  }
                }
              }

              break;
            case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
              //sending windowFn for all charts except richtext
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFn = [
                    toCamelCase(el.windowfn.percentage),
                    ['First', 'Last'].includes(el.windowfn.standingSlidingReferenceWn)
                      ? toCamelCase(el.windowfn.standingSlidingReferenceWn)
                      : toCamelCase(el.windowfn.standingSlidingAggregation),
                  ];
                }
              } else {
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(el.windowfn.percentage),
                      ['First', 'Last'].includes(el.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(el.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(el.windowfn.standingSlidingAggregation),
                    ];
                  }
                }
              }

              //sending windowFnOption for all charts except richtext
              if (['PNC'].includes(el.windowfn.standingSlidingReferenceWn)) {
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [el.windowfn.standingSlidingPreInc];
                    if (![undefined, null].includes(el.windowfn.standingSlidingNextInc)) {
                      formattedField.windowFnOption.push(el.windowfn.standingSlidingNextInc);
                    }
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [el.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(el.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(el.windowfn.standingSlidingNextInc);
                      }
                    }
                  }
                }
              }

              //sending windowFnMatrix for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                } else {
                  formattedField.windowFnMatrix = [
                    _chartAxes[1].fields.length,
                    _chartAxes[2].fields.length,
                  ];
                }
              }

              //sending windowFnPartition for two dimensional charts
              if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                  formattedField.windowFnPartition = [el.windowfn.standingSlidingRowIndex];
                } else {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                    formattedField.windowFnPartition = [el.windowfn.standingSlidingColumnIndex];
                  } else {
                    if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        el.windowfn.standingSlidingRowIndex,
                        el.windowfn.standingSlidingColumnIndex,
                        ['rowwise'].includes(el.windowfn.standingSlidingSlideDirection) ? 0 : 1,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                      }
                    }
                  }
                }
              } else {
                //sending windowFnPartition for one dimensional charts
                if (
                  !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                ) {
                  if (_chartAxes[1].fields.length === 0) {
                  } else {
                    formattedField.windowFnPartition = [el.windowfn.standingSlidingRowIndex];
                  }
                }
              }
              break;
          }
        }
        return formattedField;
      });
      if (
        ['funnel', 'gauge', 'simplecard', 'richText'].includes(
          chartProperties.properties[`${tabTileID}`].chartType,
        )
      ) {
        payLoad = [
          {
            dimensions: [],
            filterPanels: filterPanels,
            measures: non_override_measures_for_payload,
          },
        ];

        overrideMeasureFields.forEach((field: any, idx: number) => {
          const chartFilters = field.override.filter(
            (axes: AxesValuProps) => axes.name === 'Filter',
          );
          const formattedField = {
            dataType: field.dataType,
            measureId: field.uId,
            fieldName: field.fieldname,
            displayName: field.displayname,
            rollupDepth: false,
            measureOrder: field.measureOrder,
            aggr: field.agg,
            tableId: field.tableId,
            ...getCalculationByUid(
              field.SavedCalculationUUID,
              chartType,
              chartProp.chartAxes,
              savedCalculations,
            ),
            ...(field.windowfn
              ? {
                  windowFn: [] as any[],
                  windowFnOption: [] as any[],
                  windowFnMatrix: [] as any[],
                  windowFnPartition: [] as any[],
                }
              : {}),
          };
          if (field.windowfn) {
            //Function used to convert all the values of windowFunction in camelCase
            function toCamelCase(str: any) {
              return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                  return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                .replace(/\s+/g, '');
            }

            switch (field.windowfn.windowFnOptions) {
              case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.rank),
                      toCamelCase(field.windowfn.order),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.rank),
                        toCamelCase(field.windowfn.order),
                      ];
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.standingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingRowIndex,
                          field.windowfn.standingColumnIndex,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                    }
                  }
                }

                break;
              case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.slidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.slidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                    if (![undefined, null].includes(field.windowfn.slidingNextInc))
                      formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                      if (![undefined, null].includes(field.windowfn.slidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.slidingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.slidingRowIndex,
                          field.windowfn.slidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.slidingSlideDirection) ? 0 : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                    }
                  }
                }

                break;
              case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.percentage),
                      ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(field.windowfn.standingSlidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.percentage),
                        ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                          ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                          : toCamelCase(field.windowfn.standingSlidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['PNC'].includes(field.windowfn.standingSlidingReferenceWn)) {
                  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                      }
                    }
                  } else {
                    if (
                      !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(
                        chartType,
                      )
                    ) {
                      if (_chartAxes[1].fields.length === 0) {
                      } else {
                        formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                        if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                          formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                        }
                      }
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        field.windowfn.standingSlidingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingSlidingRowIndex,
                          field.windowfn.standingSlidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.standingSlidingSlideDirection)
                            ? 0
                            : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                    }
                  }
                }
                break;
            }
          }
          let measures = [formattedField];
          const filterPanels: IFilterPanel[] = [];
          const appliedChartFilters = chartFilters[0]?.fields;
          const payloadChartFilter: IFilter[] = [];
          appliedChartFilters?.forEach((_filter: any) => {
            if (
              _filter.fieldtypeoption === 'Pick List' &&
              (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
            ) {
              if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
            }
            if (
              _filter.exprType === 'between' &&
              (isInvalidValue(_filter.greaterThanOrEqualTo) ||
                isInvalidValue(_filter.lessThanOrEqualTo))
            ) {
              return false;
            } else if (_filter.exprType === 'blank') return true;
            else if (
              _filter.exprType !== 'between' &&
              _filter.fieldtypeoption === 'Search Condition' &&
              isInvalidValue(_filter.exprInput)
            ) {
              return false;
            }
            payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
          });
          if (payloadChartFilter.length > 0) {
            filterPanels.push({
              panelName: 'chartFilters',
              shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
              filters: payloadChartFilter,
            });
          }
          payLoad.push({
            dimensions: [],
            filterPanels: filterPanels,
            measures: measures,
          });
        });
      } else {
        const dimensions = chartProperties.properties[`${tabTileID}`].chartAxes.filter(
          (axes: AxesValuProps) => !['Measure', 'Filter', 'X Axis', 'Y Axis'].includes(axes.name),
        );

        payLoad = [
          {
            measures: non_override_measures_for_payload,
            dimensions: dimensions.flatMap((axes: AxesValuProps) => {
              let dimensionType: TDimensionType = 'dim';

              if (axes.name === AxisType.Row) dimensionType = DimensionType.ROW;
              else if (axes.name === AxisType.Column) dimensionType = DimensionType.COLUMN;
              else if (axes.name === AxisType.Dimension) dimensionType = DimensionType.DIMENSION;
              else if (axes.name === AxisType.SplitBy) dimensionType = DimensionType.SPLIT;
              else dimensionType = DimensionType.DIMENSION;
              return axes.fields.map((filter: any) => ({
                dataType: filter.dataType,
                dimensionId: filter.uId,
                fieldName: filter.fieldname,
                displayName: filter.displayname,
                tableId: filter.tableId,
                rollupDepth: false,
                timeGrain: filter.timeGrain,
                dimensionType,
                ...getCalculationByUid(
                  filter.SavedCalculationUUID,
                  chartType,
                  dimensions,
                  savedCalculations,
                ),
              }));
            }),
            filterPanels: filterPanels,
          },
        ];
        overrideMeasureFields.forEach((field: any, idx: number) => {
          const dimensions = field.override.filter(
            (axes: AxesValuProps) => !['Measure', 'Filter', 'X Axis', 'Y Axis'].includes(axes.name),
          );
          const chartFilters = field.override.filter(
            (axes: AxesValuProps) => axes.name === 'Filter',
          );
          const formattedField = {
            dataType: field.dataType,
            measureId: field.uId,
            fieldName: field.fieldname,
            displayName: field.displayname,
            rollupDepth: false,
            measureOrder: field.measureOrder,
            aggr: field.agg,
            tableId: field.tableId,
            ...getCalculationByUid(
              field.SavedCalculationUUID,
              chartType,
              chartProp.chartAxes,
              savedCalculations,
            ),
            ...(field.windowfn
              ? {
                  windowFn: [] as any[],
                  windowFnOption: [] as any[],
                  windowFnMatrix: [] as any[],
                  windowFnPartition: [] as any[],
                }
              : {}),
          };
          if (field.windowfn) {
            //Function used to convert all the values of windowFunction in camelCase
            function toCamelCase(str: any) {
              return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                  return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                .replace(/\s+/g, '');
            }

            switch (field.windowfn.windowFnOptions) {
              case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.rank),
                      toCamelCase(field.windowfn.order),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.rank),
                        toCamelCase(field.windowfn.order),
                      ];
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.standingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingRowIndex,
                          field.windowfn.standingColumnIndex,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                    }
                  }
                }

                break;
              case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.slidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.slidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                    if (![undefined, null].includes(field.windowfn.slidingNextInc))
                      formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                      if (![undefined, null].includes(field.windowfn.slidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.slidingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.slidingRowIndex,
                          field.windowfn.slidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.slidingSlideDirection) ? 0 : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                    }
                  }
                }

                break;
              case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.percentage),
                      ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(field.windowfn.standingSlidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.percentage),
                        ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                          ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                          : toCamelCase(field.windowfn.standingSlidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['PNC'].includes(field.windowfn.standingSlidingReferenceWn)) {
                  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                        formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                      }
                    }
                  } else {
                    if (
                      !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(
                        chartType,
                      )
                    ) {
                      if (_chartAxes[1].fields.length === 0) {
                      } else {
                        formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                        if (![undefined, null].includes(field.windowfn.standingSlidingNextInc)) {
                          formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                        }
                      }
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        field.windowfn.standingSlidingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingSlidingRowIndex,
                          field.windowfn.standingSlidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.standingSlidingSlideDirection)
                            ? 0
                            : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                    }
                  }
                }
                break;
            }
          }
          let measures = [formattedField];
          const filterPanels: IFilterPanel[] = [];
          const appliedChartFilters = chartFilters[0]?.fields;
          const payloadChartFilter: IFilter[] = [];
          appliedChartFilters?.forEach((_filter: any) => {
            if (
              _filter.fieldtypeoption === 'Pick List' &&
              (_filter.userSelection?.length === 0 || _filter.userSelection?.includes('(All)'))
            ) {
              if (_filter.filterTypeTillDate === 'enabled' && !_filter.exprTypeTillDate) return;
            }
            if (
              _filter.exprType === 'between' &&
              (isInvalidValue(_filter.greaterThanOrEqualTo) ||
                isInvalidValue(_filter.lessThanOrEqualTo))
            ) {
              return false;
            } else if (_filter.exprType === 'blank') return true;
            else if (
              _filter.exprType !== 'between' &&
              _filter.fieldtypeoption === 'Search Condition' &&
              isInvalidValue(_filter.exprInput)
            ) {
              return false;
            }
            payloadChartFilter.push(modifyFilter(_filter, savedCalculations));
          });
          if (payloadChartFilter.length > 0) {
            filterPanels.push({
              panelName: 'chartFilters',
              shouldAllConditionsMatch: !appliedChartFilters[0].any_condition_match,
              filters: payloadChartFilter,
            });
          }
          payLoad.push({
            dimensions: dimensions.flatMap((axes: AxesValuProps) => {
              let dimensionType: TDimensionType = 'dim';

              if (axes.name === AxisType.Row) dimensionType = DimensionType.ROW;
              else if (axes.name === AxisType.Column) dimensionType = DimensionType.COLUMN;
              else if (axes.name === AxisType.Dimension) dimensionType = DimensionType.DIMENSION;
              else if (axes.name === AxisType.SplitBy) dimensionType = DimensionType.SPLIT;
              else dimensionType = DimensionType.DIMENSION;
              return axes.fields.map((filter: any) => ({
                dataType: filter.dataType,
                dimensionId: filter.uId,
                fieldName: filter.fieldname,
                displayName: filter.displayname,
                tableId: filter.tableId,
                rollupDepth: false,
                timeGrain: filter.timeGrain,
                dimensionType,
                ...getCalculationByUid(
                  filter.SavedCalculationUUID,
                  chartType,
                  dimensions,
                  savedCalculations,
                ),
              }));
            }),
            filterPanels: filterPanels,
            measures: measures,
          });
        });
      }
      if (serverCall) {
        (async () => {
          await fetchDataForTile(tabTileID, payLoad, chartProp, link);
        })();
      }
      if (screenFrom === 'TV_Display' && refreshTime > 0 && dataRefreshEnableds) {
        if (intervalRef.current[tabTileID]) {
          clearInterval(intervalRef.current[tabTileID]);
        }

        intervalRef.current[tabTileID] = setInterval(async () => {
          await fetchDataForTile(tabTileID, payLoad, chartProp, link);
        }, refreshTime);
      }
    });
    dispatch(callQueryAPIDashboard(false));
  };

  useEffect(() => {
    (async function () {
      if (screenFrom === 'Dashboard') {
        (async () => {
          await fetchDataforDashboard(tabId);
        })();
        // await fetchDataforDashboard(tabId);
      } else if (screenFrom === 'TV_Display') {
        selectedTabArray.forEach((tabId: number) => {
          fetchDataforDashboard(tabId);
        });
      } else {
        if (
          // tabTileProps.previousTabId === 0 ||
          // tabTileProps.previousTileId === 0 ||
          // chartProp.axesEdited ||
          // chartGroup.chartFilterGroupEdited ||
          // dashBoardGroup.dashBoardGroupEdited ||
          // tabTileProps.selectedTileId ||
          chartControls.properties[_propKey].callQueryAPI
        ) {
          const debouncedMakeServiceCall = debounce(async () => {
            await makeServiceCall(
              chartProp,
              chartControls,
              chartGroup,
              dashBoardGroup,
              tabTileProps,
              chartProperties,
              _propKey,
              dispatch,
              setSeverity,
              setOpenAlert,
              setTestMessage,
              setLoading,
              screenFrom,
              token,
              savedCalculations,
              setShowSnackBar,
              setMessage,
              tooltipFormats,
            );
          }, 1000);
          if (chartProp.chartType === 'crossTab') {
            await debouncedMakeServiceCall();
            dispatch(callQueryAPI(_propKey, false));
          } else {
            await makeServiceCall(
              chartProp,
              chartControls,
              chartGroup,
              dashBoardGroup,
              tabTileProps,
              chartProperties,
              _propKey,
              dispatch,
              setSeverity,
              setOpenAlert,
              setTestMessage,
              setLoading,
              screenFrom,
              token,
              savedCalculations,
              setShowSnackBar,
              setMessage,
              tooltipFormats,
            );
            dispatch(callQueryAPI(_propKey, false));
          }
        }
      }
      resetStore();
    })();
    //eslint-disable-next-line
  }, [
    // chartProp.chartAxes,
    // chartProp.chartType,
    // chartProp.filterRunState,
    // chartProp.axesEdited,
    // chartGroup.chartFilterGroupEdited,
    // chartGroup,
    // tabTileProps.selectedTileId,
    // dashBoardGroup.dashBoardGroupEdited,
    // tabTileProps.isDashboardTileSwitched,
    // tooltipExtraMeasureField,

    chartControls.properties[_propKey].callQueryAPI,
    // dashBoardGroup.filterGroupTabTiles,
    tabTileProps.callQueryAPIDashboard,
  ]);

  useEffect(() => {
    return () => {
      Object.values(intervalRef.current).forEach(clearInterval);
      intervalRef.current = {};
    };
  }, [screenFrom]);

  /**
   * @param dmId   dynamicMeasureId of chartProp
   * @param value  value fetched from server
   * @returns      value in selected style
   */
  const getFormatedValue = (dmId: number, value: any) => {
    let formattedValue = value;
    formattedValue = formatChartLabelValue(
      dynamicMeasureState.dynamicMeasureProps?.[dynamicMeasureState.selectedTabId]?.[
        dynamicMeasureState.selectedTileId
      ]?.[`${dynamicMeasureState.selectedTileId}.${dmId}`],
      formattedValue,
    );
    return formattedValue;
  };

  /**
   * fetches data for a tile.
   * If there is value of link then ```checkToken: link === ""``` and if not then  ```checkToken: true``` becuase in FetchData, **by default checkToken value is true**. So if default value is changed there then make sure to change here as well.
   */
  async function fetchDataForTile(
    tabTileID: string,
    payLoad: any[],
    chartProp: any,
    link?: string,
  ) {
    const _selectedDS = chartProperties.properties[`${tabTileID}`].selectedDs;
    let url = '';
    if (_selectedDS.isFlatFileData) {
      if (link && link !== '') {
        url = `open-link/query?datasetid=${_selectedDS.id}&linkId=${link}`;
      } else {
        // url = `query?datasetid=${'bH64C0NKMzU'}`;
        url = `query?datasetid=${_selectedDS.id}`;
      }
    } else {
      if (link && link !== '') {
        url = `open-link/query?dbconnectionid=${_selectedDS.connectionId}&datasetid=${_selectedDS.id}&workspaceId=${_selectedDS.workSpaceId}&linkId=${link}`;
      } else {
        url = `query?dbconnectionid=${_selectedDS.connectionId}&datasetid=${_selectedDS.id}&workspaceId=${_selectedDS.workSpaceId}`;
      }
    }
    setLoading(true);
    const res = await FetchData({
      checkToken: link ? link === '' : true,
      requestType: 'withData',
      method: 'POST',
      url: url,
      headers:
        link && link !== ''
          ? {
              'Content-Type': 'application/json',
              'X-TENANT-ID': tenantId || 'community',
            }
          : {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
      data: payLoad,
    });
    if (res.status) {
      dispatch(storeServerData(tabTileID, res.data));
      if (chartProperties.properties[tabTileID].chartType === 'richText') {
        dispatch(
          updateChartDataForDm(sortChartData(res.data, chartProp, chartControls, tabTileID)),
        );
        const values = Object.values(res.data[0]);
        dispatch(
          updateRichTextDynamicValue(
            tabTileID,
            getFormatedValue(chartProp.dynamicMeasureId, values[0]),
          ),
        );
      } else {
        if (
          typeof chartControls.properties[tabTileID].showHide?.numberInput === 'number' &&
          res.data
        ) {
          const showHide = chartControls.properties[tabTileID].showHide;
          filterShowHideData(
            showHide.operator,
            dispatch,
            res.data,
            tabTileID,
            showHide.numberInput,
            chartControls,
            chartProperties,
          );
        } else {
          dispatch(
            updateChartData(
              tabTileID,
              sortChartData(res.data, chartProp, chartControls, tabTileID),
            ),
          );
        }
      }
      setLoading(false);
    } else {
      setLoading(false);
    }

    // console.log(res);
    if (!res.status) {
      const error = {
        statusCode: res.responseStatusCode!,
        message: res.data.message || 'Something is wrong with the dataset or query',
      };
      dispatch(storeErrorDetails(tabTileID, error));
    }
    if (res.status) {
      dispatch(storeErrorDetails(tabTileID, null));
      dispatch(callQueryAPIDashboard(false));
      dispatch(callQueryAPI(tabTileID, false));
    }
  }

  const resetStore = () => {
    dispatch(toggleAxesEdited(_propKey, false));
    dispatch(canReUseData(_propKey, false));
    dispatch(chartFilterGroupEdited(false));
    dispatch(dashBoardFilterGroupsEdited(false));
    dispatch(setDashTileSwitched(false));
  };

  return {
    loading,
    message,
    severity,
  };
};

export default useChartData;
